import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { getStringTypeProps } from '@blaze-cms/utils/src/helpers';
import ListRender from './ListRender';
import { getSearchPublishedContent } from '../../application/query';
import { buildRawQuery, buildQueryFields, buildSetFilters, getGenericRenderVariables, getEntityData, getCurrentOffset, getUpdatedFilterBy, checkPropsToUse, buildPropsQuery } from '../../helpers';
import { sortAggs, getItemsPerPageToUse, buildAzAggregations, getListComponent } from './helpers';
import { SEARCH_PUBLISHED_CONTENT, INFINITE } from '../../constants';
import { RAW_RESULTS } from './constants';
const ListBuilder = props => {
  const {
    mainSchemas,
    isCard,
    isAZ,
    inheritedFilters,
    paginationType,
    entities,
    parent: {
      itemId
    },
    itemsPerPage,
    itemsToDisplay,
    limit,
    propsToDisplay,
    offset,
    entityData,
    sortby,
    sort,
    paginationIndex,
    omitWrappers,
    operator,
    filterOperator,
    currentSchema,
    filterEntitySchema,
    requiredSchema,
    searchValues,
    azFilter,
    sortProperties,
    itemListIds
  } = props;
  const [entity] = entities;
  const isInfinite = paginationType === INFINITE;
  const listComponent = getListComponent(isCard, paginationType);
  const propsToDisplayValues = checkPropsToUse(propsToDisplay);
  const entitiesTypes = entities.map(ent => getEntityData(ent, mainSchemas[ent]));
  const graphqlEntityMap = entitiesTypes.reduce((acc, entityType) => _objectSpread(_objectSpread({}, acc), {}, {
    [entityType.entityType]: entityType.entity
  }), {});
  const docTypes = entitiesTypes.map(({
    docType: type
  }) => type);
  const entityTypes = entitiesTypes.map(({
    entityType: eType
  }) => eType);
  const entityFields = buildPropsQuery(filterEntitySchema, propsToDisplayValues, props) || '';
  const itemsToDisplayToUse = itemListIds || itemsToDisplay;
  const limitToUse = limit || itemsToDisplayToUse.length;
  const shouldApplyDefaultSort = !sortProperties || !sortProperties.length;
  const itemsPerPageToUse = getItemsPerPageToUse(itemsPerPage, limitToUse);
  const initialOffset = getCurrentOffset(limitToUse, offset, itemsPerPageToUse, paginationIndex);
  const fields = buildQueryFields(entityFields, entityTypes, !isCard);
  const action = getSearchPublishedContent(fields);
  const actionKey = SEARCH_PUBLISHED_CONTENT;
  const updatedFilterBy = getUpdatedFilterBy(inheritedFilters, entityData, currentSchema, filterEntitySchema, itemId, props);
  const filterValues = updatedFilterBy && updatedFilterBy.length ? buildSetFilters(_objectSpread(_objectSpread({}, props), {}, {
    filterBy: updatedFilterBy
  })) : buildSetFilters(_objectSpread({}, props));
  const {
    relations = []
  } = filterEntitySchema;
  const stringProps = getStringTypeProps(entity, relations, Object.values(requiredSchema));
  const filterData = {
    filterValues,
    filterOperator,
    relations,
    stringProps
  };
  const azRawQuery = buildRawQuery({
    docType: docTypes,
    operator,
    searchValues,
    filterData,
    id: itemId,
    itemsToDisplay: itemsToDisplayToUse,
    shouldApplySort: shouldApplyDefaultSort,
    azOptions: {
      shouldReturnAggs: true,
      isAZ,
      sortProperties
    }
  });
  const filterProps = _objectSpread(_objectSpread({}, props), {}, {
    limit: itemsPerPageToUse,
    offset: initialOffset,
    sort,
    sortby,
    sortProperties
  });
  const propsForVariables = {
    relations,
    filterProps,
    stringProps
  };
  const azVariables = getGenericRenderVariables(_objectSpread(_objectSpread({}, propsForVariables), {}, {
    rawQuery: azRawQuery
  }));
  const azAction = getSearchPublishedContent(RAW_RESULTS);
  const {
    data,
    error,
    loading
  } = useQuery(azAction, {
    variables: azVariables,
    skip: !isAZ
  });
  if (loading) return '';
  if (error) return error.message;
  const listAggregations = isAZ ? sortAggs(data) : [];
  const updatedAggregations = buildAzAggregations(listAggregations);
  const rawQuery = buildRawQuery({
    docType: docTypes,
    operator,
    searchValues,
    filterData: _objectSpread(_objectSpread({}, filterData), {}, {
      azFilter
    }),
    id: itemId,
    itemsToDisplay: itemsToDisplayToUse,
    shouldApplySort: shouldApplyDefaultSort,
    azOptions: {
      shouldReturnAggs: false,
      isAZ,
      sortProperties
    },
    sortProperties
  });
  const variables = getGenericRenderVariables(_objectSpread(_objectSpread({}, propsForVariables), {}, {
    rawQuery
  }));
  return /*#__PURE__*/React.createElement(ListRender, _extends({
    variables: variables,
    isInfinite: isInfinite,
    initialOffset: initialOffset,
    actionKey: actionKey,
    limitToUse: limitToUse,
    itemsPerPageToUse: itemsPerPageToUse,
    action: action,
    omitWrappers: omitWrappers,
    listComponent: listComponent,
    listAggregations: updatedAggregations,
    isAZ: isAZ,
    azFilter: azFilter,
    graphqlEntityMap: graphqlEntityMap
  }, props, {
    itemsToDisplay: itemsToDisplayToUse
  }));
};
ListBuilder.propTypes = {
  entities: PropTypes.array.isRequired,
  mainSchemas: PropTypes.object.isRequired,
  isCard: PropTypes.bool.isRequired,
  isAZ: PropTypes.bool.isRequired,
  parent: PropTypes.object.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  paginationIndex: PropTypes.number.isRequired,
  propsToDisplay: PropTypes.array.isRequired,
  currentSchema: PropTypes.object.isRequired,
  filterEntitySchema: PropTypes.object.isRequired,
  requiredSchema: PropTypes.object.isRequired,
  searchValues: PropTypes.object.isRequired,
  inheritedFilters: PropTypes.array,
  entityData: PropTypes.object,
  itemsToDisplay: PropTypes.array,
  limit: PropTypes.number,
  omitWrappers: PropTypes.bool,
  offset: PropTypes.number,
  sortby: PropTypes.array,
  sort: PropTypes.string,
  operator: PropTypes.string.isRequired,
  filterOperator: PropTypes.string.isRequired,
  paginationType: PropTypes.string,
  azFilter: PropTypes.string,
  sortProperties: PropTypes.array,
  itemListIds: PropTypes.array
};
ListBuilder.defaultProps = {
  sort: '',
  sortby: [],
  entityData: {},
  itemsToDisplay: [],
  itemListIds: null,
  limit: 0,
  omitWrappers: false,
  offset: 0,
  paginationType: 'infinite',
  inheritedFilters: [],
  azFilter: '',
  sortProperties: []
};
export default ListBuilder;
//# sourceMappingURL=ListBuilder.js.map